// A per-agent avatar — the agent's visual identity / mascot. If the agent has a chosen
// mascot (an emoji, or an uploaded image URL/data-URI stored in `avatar`) we show that;
// otherwise we fall back to initials over a stable colour derived from the agent id, so
// the same agent always looks the same across the app.

import { useId } from "react";

import { agentHueFrom, AGENT_PRESET_MASCOT_PATHS, presetMascotBody } from "./agentHue";

/** Up to two initials from the agent's name. */
function initials(name: string): string {
  const parts = name
    .trim()
    .split(/[\s—–-]+/)
    .filter(Boolean);
  if (parts.length === 0) return "•";
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
}

/** Treat a value as an image source (preset illustration / uploaded mascot) vs an
 * emoji/text mascot. Covers absolute URLs, data/blob URIs, and app-relative paths
 * (e.g. "/brand/agents/blue.png"). */
export function isImage(v: string): boolean {
  const s = v.trim();
  return /^(https?:\/\/|data:image\/|blob:|\/)/.test(s) || /\.(png|jpe?g|webp|gif|svg)$/i.test(s);
}

/**
 * A Morrow agent character, drawn as SVG: a soft squircle body in the agent's colour with two
 * eyes. The glow is a CSS drop-shadow in the same colour, so it sits correctly on any theme
 * (the old PNGs baked a white halo in, which showed as a bright box on dark backgrounds).
 * The eyes blink now and then, and glance side to side while the agent is thinking.
 */
export function MascotSvg({
  color,
  size,
  thinking = false,
  glow = true,
}: {
  readonly color: string;
  /** Height in px; the character is slightly wider than tall. */
  readonly size: number;
  readonly thinking?: boolean;
  readonly glow?: boolean;
}) {
  const id = useId();
  const body = `${id}-body`;
  const sheen = `${id}-sheen`;
  return (
    <svg
      aria-hidden
      viewBox="0 0 100 88"
      width={size * (100 / 88)}
      height={size}
      className="block shrink-0 overflow-visible"
      style={
        glow
          ? {
              filter: `drop-shadow(0 ${size * 0.06}px ${size * 0.16}px color-mix(in oklab, ${color} 55%, transparent))`,
            }
          : undefined
      }
    >
      <defs>
        <linearGradient id={body} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={`color-mix(in oklab, ${color} 92%, white)`} />
          <stop offset="58%" stopColor={color} />
          <stop offset="100%" stopColor={`color-mix(in oklab, ${color} 55%, white)`} />
        </linearGradient>
        <linearGradient id={sheen} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="white" stopOpacity="0.45" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect x="6" y="6" width="88" height="76" rx="28" fill={`url(#${body})`} />
      <rect x="14" y="10" width="72" height="26" rx="13" fill={`url(#${sheen})`} />
      <g className="agent-mascot-eyes" data-thinking={thinking}>
        <ellipse cx="41" cy="42" rx="7.5" ry="10.5" fill="white" />
        <ellipse cx="61" cy="39" rx="7.5" ry="10.5" fill="white" />
        <ellipse cx="41" cy="49.5" rx="5.5" ry="2.4" fill="black" opacity="0.07" />
        <ellipse cx="61" cy="46.5" rx="5.5" ry="2.4" fill="black" opacity="0.07" />
      </g>
    </svg>
  );
}

export function AgentAvatar({
  name,
  seed,
  avatar,
  size = 32,
  round = false,
}: {
  readonly name: string;
  /** Stable seed (the agent id); falls back to the name. */
  readonly seed?: string;
  /** Chosen mascot: an emoji, or an image URL / data-URI. Empty ⇒ initials fallback. */
  readonly avatar?: string | null;
  readonly size?: number;
  /** Circular (WhatsApp-style header) vs rounded square (lists). */
  readonly round?: boolean;
}) {
  const hue = agentHueFrom(avatar, seed || name || "agent");
  const radius = round ? "9999px" : `${Math.max(6, Math.round(size * 0.22))}px`;
  const mascot = avatar?.trim() || "";

  const presetBody = mascot ? presetMascotBody(mascot) : null;
  if (presetBody) {
    // Preset characters are their own shape — never cropped into a circle.
    return (
      <span
        aria-hidden
        className="inline-flex shrink-0 items-center justify-center"
        style={{ width: size, height: size }}
      >
        <MascotSvg color={presetBody} size={size * 0.82} glow={size >= 40} />
      </span>
    );
  }

  if (mascot && isImage(mascot)) {
    return (
      <img
        src={mascot}
        alt=""
        aria-hidden
        className="shrink-0 object-cover"
        style={{ width: size, height: size, borderRadius: radius }}
      />
    );
  }

  const isEmoji = mascot.length > 0 && !isImage(mascot);
  return (
    <span
      aria-hidden
      className="inline-flex shrink-0 items-center justify-center font-semibold text-white"
      style={{
        width: size,
        height: size,
        borderRadius: radius,
        fontSize: Math.round(size * (isEmoji ? 0.58 : 0.4)),
        background: isEmoji
          ? `hsl(${hue} 30% 20%)`
          : `linear-gradient(135deg, hsl(${hue} 62% 48%), hsl(${(hue + 28) % 360} 60% 38%))`,
      }}
    >
      {isEmoji ? mascot : initials(name)}
    </span>
  );
}

/** The preset mascots offered in the create/settings pickers — the Morrow agent
 * illustrations (the coloured characters shipped in public/brand/agents). Users can also
 * paste an emoji or upload/point to their own image. */
export const PRESET_MASCOTS = AGENT_PRESET_MASCOT_PATHS;
